import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form, Input } from "antd";

import { useDispatch } from "react-redux";
import { authValidate, authRequest } from "../../redux/actions/authAction";
import { workEmailFormRules } from "../../utils/workEmail";

const RESEND_COOLDOWN_SECONDS = 60;

export default function FormSignup({
  lockedEmail,
  onAuthenticated,
  compact = false,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const [generateOTP, setGenerateOTP] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [resendCountdownActive, setResendCountdownActive] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (lockedEmail) {
      form.setFieldsValue({ emailAddress: lockedEmail });
    }
  }, [lockedEmail, form]);

  const startResendCountdown = () => {
    setResendCountdown(RESEND_COOLDOWN_SECONDS);
    setResendCountdownActive(true);
  };

  useEffect(() => {
    if (!resendCountdownActive || resendCountdown <= 0) return;
    const id = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          setResendCountdownActive(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [resendCountdownActive, resendCountdown]);

  const finishAuth = () => {
    if (onAuthenticated) {
      Promise.resolve(onAuthenticated())
        .then(() => setLoading(false))
        .catch(() => setLoading(false));
      return;
    }
    setTimeout(() => {
      navigate("/app");
      setLoading(false);
      window.location.reload();
    }, 1000);
  };

  const handleResendOTP = () => {
    const emailAddress = lockedEmail || form.getFieldValue("emailAddress");
    const firstName = form.getFieldValue("firstName");
    const lastName = form.getFieldValue("lastName");
    const phoneNumber = form.getFieldValue("phoneNumber");
    if (!emailAddress || resendCountdown > 0) return;
    setLoading(true);
    dispatch(
      authRequest({
        emailAddress,
        firstName,
        lastName,
        phoneNumber,
      }),
    )
      .then(() => {
        startResendCountdown();
        setLoading(false);
      })
      .catch(() => {
        setTimeout(() => setLoading(false), 1000);
      });
  };

  const onSubmit = (values) => {
    setLoading(true);
    if (generateOTP) {
      dispatch(authValidate(values))
        .then(finishAuth)
        .catch(() => {
          setTimeout(() => setLoading(false), 1000);
        });
    } else {
      dispatch(authRequest(values))
        .then(() => {
          setGenerateOTP(true);
          startResendCountdown();
          setLoading(false);
        })
        .catch(() => {
          setTimeout(() => setLoading(false), 1000);
        });
    }
  };

  return (
    <Form
      form={form}
      initialValues={lockedEmail ? { emailAddress: lockedEmail } : {}}
      onFinish={onSubmit}
      style={
        compact
          ? { marginTop: 0, width: "100%" }
          : { marginTop: "3rem", width: "min(400px, calc(100vw - 3rem))" }
      }
      layout="vertical"
    >
      <Form.Item
        name="firstName"
        label="First name"
        rules={[
          {
            required: true,
            message: "Please enter your first name",
          },
        ]}
      >
        <Input placeholder="First name" />
      </Form.Item>
      <Form.Item
        name="lastName"
        label="Last name"
        rules={[
          {
            required: true,
            message: "Please enter your last name",
          },
        ]}
      >
        <Input placeholder="Last name" />
      </Form.Item>
      <Form.Item
        name="phoneNumber"
        label="Phone number"
        rules={[
          {
            required: true,
            message: "Please enter your phone number",
          },
          {
            validator: (_, value) => {
              if (!value) {
                return Promise.resolve();
              }
              const digits = value.replace(/\D/g, "");
              if (digits.length < 10 || digits.length > 15) {
                return Promise.reject(
                  new Error("Please enter a valid phone number"),
                );
              }
              if (!/^[0-9+\-()\s]+$/.test(value)) {
                return Promise.reject(
                  new Error("Phone number can only contain digits and symbols"),
                );
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input placeholder="Phone number" />
      </Form.Item>
      <Form.Item name="emailAddress" label="Work email" rules={workEmailFormRules}>
        <Input
          placeholder="you@company.com"
          disabled={Boolean(lockedEmail)}
          onChange={() => setGenerateOTP(false)}
        />
      </Form.Item>
      {generateOTP && (
        <Form.Item name="oneTimePassword" label="One-time password">
          <Input.OTP
            placeholder="Enter the 6-digit code"
            disabled={!generateOTP}
            size="large"
            style={{ width: "100%" }}
          />
        </Form.Item>
      )}
      {generateOTP && (
        <Form.Item style={{ marginBottom: 16 }}>
          <Button
            type="link"
            onClick={handleResendOTP}
            disabled={resendCountdown > 0 || isLoading}
            style={{ padding: 0 }}
          >
            {resendCountdown > 0
              ? `Resend OTP in ${resendCountdown}s`
              : "Resend OTP"}
          </Button>
        </Form.Item>
      )}
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={isLoading} block>
          {generateOTP ? "Complete signup" : "Send OTP"}
        </Button>
      </Form.Item>
    </Form>
  );
}
